import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "./CustomerForm.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_URL } from "../../constant";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

const CustomerForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState(null);
  const formRef = useRef(null);
  const recaptchaRef = useRef(null);
  
  // Add iOS detection state
  const [isIOS, setIsIOS] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    country_name: "United Kingdom",
    region: "England - City of London",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    city: "",
    password: "",
    password_confirmation: "",
    showPassword: false,
    showConfirmPassword: false,
  });

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    password_confirmation: "",
    address_line1: "",
    city: "",
  });

  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [touched, setTouched] = useState({
    first_name: false,
    last_name: false,
    email: false,
    phone_number: false,
    password: false,
    password_confirmation: false,
    address_line1: false,
    city: false,
  });
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // Get referral code from URL parameter and persist it
  const [referralCode, setReferralCode] = useState(null);

  // Detect iOS on mount
  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    console.log('iOS device detected:', iOS);
  }, []);

  // Initialize referral code from URL or localStorage on component mount
  useEffect(() => {
    // First try to get from URL
    const urlRef = searchParams.get('ref');
    
    if (urlRef) {
      // Store in localStorage for persistence
      localStorage.setItem('customer_referral_code', urlRef);
      setReferralCode(urlRef);
      console.log('Customer referral code from URL:', urlRef);
    } else {
      // Check if we have a stored referral code
      const storedRef = localStorage.getItem('customer_referral_code');
      if (storedRef) {
        setReferralCode(storedRef);
        console.log('Customer referral code from localStorage:', storedRef);
      }
    }
  }, [searchParams]);

  // captcha handler
  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  // Container styles
  const containerStyle = {
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    marginTop: "30px",
  };

  const formWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    padding: "0px",
    flex: 1,
    boxSizing: "border-box",
    marginTop: "120px",
    width: "100%",
    marginBottom: "120px",
  };

  const regionOptions = {
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
  };

  const formCardStyle = {
    backgroundColor: "#ffffff",
    padding: "32px",
    borderRadius: "17.6px",
    width: "100%",
    maxWidth: "1500px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
  };

  const boxStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #dcdcdc",
    borderRadius: "17.6px",
    padding: "20px",
    marginBottom: "16px",
    boxSizing: "border-box",
  };

  const headingStyle = {
    textAlign: "center",
    fontSize: "28px",
    fontWeight: "900",
    marginBottom: "4px",
    background: "linear-gradient(90deg, #2D4CA8 52%, #9F4AFF 60%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const subheadingStyle = {
    textAlign: "center",
    color: "#063b8a",
    fontWeight: "700",
    fontSize: "14px",
    marginBottom: "24px",
  };

  const sectionTitleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#052C65",
    marginBottom: "12px",
  };

  const labelStyle = {
    fontWeight: "medium",
    fontSize: "14px",
    color: "#052C65",
    marginBottom: "6px",
    display: "block",
  };

  const inputRowStyle = {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  };

  const inputRowStyleChild = {
    flex: "1",
    minWidth: "200px",
  };

  const phoneInputStyle = {
    width: "100%",
    height: "40px",
    padding: "9px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    fontSize: "16px", // Increased for iOS to prevent zoom
    boxSizing: "border-box",
    paddingLeft: "52px",
  };

  const inputStyle = {
    width: "100%",
    height: "40px",
    padding: "9px",
    borderRadius: "12px",
    border: "1px solid #ccc",
    fontSize: "16px", // Increased for iOS to prevent zoom
    boxSizing: "border-box",
  };

  const errorInputStyle = {
    ...inputStyle,
    border: "1px solid #d32f2f",
  };

  const selectStyle = {
    ...inputStyle,
  };

  const checkboxContainerStyle = {
    display: "flex",
    gap: "10px",
    fontSize: "13px",
    color: "#000080",
    marginBottom: "16px",
  };

  const buttonStyle = {
    width: "40%",
    padding: "12px",
    backgroundColor: "#000080",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: "500",
    cursor: "pointer",
    fontSize: "16px",
    WebkitAppearance: "none", // iOS fix
    MozAppearance: "none", // Firefox fix
    appearance: "none",
    transition: "background-color 0.3s",
    touchAction: "manipulation", // Improves touch responsiveness
  };

  const linkStyle = {
    color: "#4f46e5",
    textDecoration: "underline",
  };

  const loginLinkContainerStyle = {
    textAlign: "center",
    marginTop: "16px",
    fontSize: "16px",
    color: "#052C65",
  };

  const loginLinkStyle = {
    color: "#052C65",
    fontWeight: "700",
    textDecoration: "none",
    cursor: "pointer",
  };

  const errorStyle = {
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
  };

  const handleRegionChange = (e) => {
    const { name, value } = e.target;

    if (name === "country_name") {
      setFormData((prev) => ({
        ...prev,
        country_name: value,
        region: regionOptions[value]?.[0] || "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validation functions
  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.length < 3) return "First name must be at least 3 characters";
    if (value.length > 50) return "First name must not exceed 50 characters";
    return "";
  };

  const validateLastName = (value) => {
    if (!value.trim()) return "Last name is required";
    if (value.length < 2) return "Last name must be at least 2 characters";
    if (value.length > 50) return "Last name must not exceed 50 characters";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    if (value.length > 255) return "Email must not exceed 255 characters";
    return "";
  };

  const validatePhone = (value) => {
    if (!value) return "Phone number is required";
    if (value.replace(/\D/g, "").length < 10)
      return "Please enter a valid phone number";
    if (value.length > 20) return "Phone number must not exceed 20 characters";
    return "";
  };

  const validateAddressLine1 = (value) => {
    if (!value.trim()) return "Address line 1 is required";
    if (value.length > 100) return "Address line 1 must not exceed 100 characters";
    return "";
  };

  const validateCity = (value) => {
    if (!value.trim()) return "City is required";
    if (value.length > 100) return "City must not exceed 100 characters";
    return "";
  };

  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters long";
    if (value.length > 50) return "Password must not exceed 50 characters";

    const strongRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!strongRegex.test(value)) {
      return "Password should include uppercase, lowercase, number and special character";
    }

    return "";
  };

  const validatePasswordConfirmation = (value) => {
    if (!value) return "Please confirm your password";
    if (value !== formData.password) return "Passwords do not match";
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched[name]) {
      let error = "";

      switch (name) {
        case "first_name":
          error = validateFirstName(value);
          break;
        case "last_name":
          error = validateLastName(value);
          break;
        case "email":
          error = validateEmail(value);
          break;
        case "password":
          error = validatePassword(value);
          break;
        case "password_confirmation":
          error = validatePasswordConfirmation(value);
          break;
        case "address_line1":
          error = validateAddressLine1(value);
          break;
        case "city":
          error = validateCity(value);
          break;
        default:
          break;
      }

      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }

    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    let error = "";

    switch (name) {
      case "first_name":
        error = validateFirstName(value);
        break;
      case "last_name":
        error = validateLastName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "password":
        error = validatePassword(value);
        break;
      case "password_confirmation":
        error = validatePasswordConfirmation(value);
        break;
      case "address_line1":
        error = validateAddressLine1(value);
        break;
      case "city":
        error = validateCity(value);
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: value,
    }));

    if (touched.phone_number) {
      const error = validatePhone(value);
      setErrors((prev) => ({
        ...prev,
        phone_number: error,
      }));
    }

    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  const handlePhoneBlur = () => {
    setTouched((prev) => ({
      ...prev,
      phone_number: true,
    }));

    const error = validatePhone(formData.phone_number);
    setErrors((prev) => ({
      ...prev,
      phone_number: error,
    }));
  };

  const validateForm = () => {
    console.log("Validating form...");
    
    const newErrors = {
      first_name: validateFirstName(formData.first_name),
      last_name: validateLastName(formData.last_name),
      email: validateEmail(formData.email),
      phone_number: validatePhone(formData.phone_number),
      password: validatePassword(formData.password),
      password_confirmation: validatePasswordConfirmation(formData.password_confirmation),
    };

    setErrors(newErrors);

    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone_number: true,
      password: true,
      password_confirmation: true,
    });

    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    console.log("Validation errors:", newErrors, "Has errors:", hasErrors);

    if (!agreedToTerms) {
      setMessage({
        text: "Please accept our Privacy Policy and Terms & Conditions",
        type: "error",
      });
      return false;
    }

    if (!captchaToken) {
      setMessage({
        text: "Please verify that you are not a robot.",
        type: "error",
      });
      return false;
    }

    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For iOS, also prevent default on the native event
    if (e.nativeEvent) {
      e.nativeEvent.preventDefault();
      e.nativeEvent.stopPropagation();
    }
    
    console.log('Submit triggered on iOS device:', isIOS);
    console.log('Event type:', e.type);
    console.log('Form data:', formData);
    
    if (isLoading) {
      console.log("Already loading, skipping submission");
      return;
    }

    // Force validation before submission
    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      console.log("Starting registration process...");
      
      localStorage.setItem("tempPassword", formData.password);

      let apiUrl = `${API_URL}/api/customer/register`;
      
      const queryParams = new URLSearchParams();
      if (referralCode) {
        queryParams.append('ref', referralCode);
      }
      
      const queryString = queryParams.toString();
      if (queryString) {
        apiUrl += `?${queryString}`;
      }

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        country_name: formData.country_name,
        region: formData.region,
        address_line1: formData.address_line1 || "",
        address_line2: formData.address_line2 || "",
        address_line3: formData.address_line3 || "",
        city: formData.city || "",
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        "g-recaptcha-response": captchaToken,
      };

      console.log('Submitting customer registration with referral code:', referralCode);
      console.log('API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("API Response:", data);

      if (response.ok) {
        setMessage({
          text: "Account created successfully! Please check your email for verification.",
          type: "success",
        });

        localStorage.setItem("verifyEmail", formData.email);
        localStorage.removeItem('customer_referral_code');

        setTimeout(() => {
          navigate("/verify-email-c");
        }, 2000);
      } else {
        if (data.errors) {
          const firstError = Object.values(data.errors)[0][0];
          setMessage({
            text: firstError,
            type: "error",
          });
        } else {
          setMessage({
            text: data.message || "Failed to create account. Please try again.",
            type: "error",
          });
        }
        localStorage.removeItem("tempPassword");
        
        // Reset reCAPTCHA on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setCaptchaToken(null);
        }
      }
    } catch (error) {
      console.error('Customer registration error:', error);
      setMessage({
        text: "Network error. Please check your connection and try again.",
        type: "error",
      });
      localStorage.removeItem("tempPassword");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle touch end specifically for iOS
  const handleTouchEnd = (e) => {
    if (isIOS) {
      e.preventDefault();
      // For iOS, manually trigger form submission
      if (!isLoading && formRef.current) {
        // Create and dispatch a submit event
        const submitEvent = new Event('submit', { 
          bubbles: true, 
          cancelable: true 
        });
        formRef.current.dispatchEvent(submitEvent);
      }
    }
    if (!isLoading) {
      setIsButtonHovered(false);
    }
  };

  return (
    <div style={{ ...containerStyle, minHeight: "auto" }}>
      <Header />

      <div style={formWrapperStyle}>
        <div
          className="form-card p-3 m-3 pt-4 h-auto"
          style={{ ...formCardStyle, minHeight: "auto", maxWidth: "900px" }}
        >
          <h2 style={headingStyle}>Create Account</h2>
          <p style={subheadingStyle}>
            Enter your details below to set up your account
          </p>

          {message.text && (
            <div
              style={{
                textAlign: "center",
                padding: "10px",
                borderRadius: "8px",
                margin: "20px 0",
                fontWeight: "500",
                backgroundColor:
                  message.type === "error" ? "#ffebee" : "#e8f5e9",
                color: message.type === "error" ? "#c62828" : "#2e7d32",
              }}
            >
              {message.text}
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            {/* Name Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Name
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>First name</label>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    style={errors.first_name ? errorInputStyle : inputStyle}
                    disabled={isLoading}
                  />
                  {errors.first_name && (
                    <span style={errorStyle}>{errors.first_name}</span>
                  )}
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Last name</label>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Wick"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    style={errors.last_name ? errorInputStyle : inputStyle}
                    disabled={isLoading}
                  />
                  {errors.last_name && (
                    <span style={errorStyle}>{errors.last_name}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Contact
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="johnwick01@gmail.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    style={errors.email ? errorInputStyle : inputStyle}
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <span style={errorStyle}>{errors.email}</span>
                  )}
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Phone number</label>
                  <PhoneInput
                    country={"gb"}
                    value={formData.phone_number}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    inputStyle={
                      errors.phone_number
                        ? { ...phoneInputStyle, border: "1px solid #d32f2f" }
                        : phoneInputStyle
                    }
                    buttonStyle={{ borderRadius: "12px 0 0 12px" }}
                    disabled={isLoading}
                    inputProps={{
                      name: 'phone',
                      required: true,
                      autoComplete: 'tel',
                    }}
                  />
                  {errors.phone_number && (
                    <span style={errorStyle}>{errors.phone_number}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Address Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Address
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Country</label>
                  <select
                    name="country_name"
                    value={formData.country_name}
                    onChange={handleRegionChange}
                    style={selectStyle}
                    disabled={isLoading}
                  >
                    <option>United Kingdom</option>
                    <option>India</option>
                    <option>Sri Lanka</option>
                    <option>Canada</option>
                    <option>Iran</option>
                  </select>
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Region</label>
                  <select
                    name="region"
                    value={formData.region}
                    onChange={handleRegionChange}
                    style={selectStyle}
                    disabled={isLoading}
                  >
                    {(regionOptions[formData.country_name] || []).map(
                      (region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Security Box */}
            <div style={boxStyle} className="mb-2 p-3">
              <div style={sectionTitleStyle} className="mb-0">
                Security
              </div>
              <div style={inputRowStyle} className="gap-1 gap-md-3">
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={formData.showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={errors.password ? errorInputStyle : inputStyle}
                      disabled={isLoading}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        padding: "10px", // Increased touch target for iOS
                      }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          showPassword: !prev.showPassword,
                        }))
                      }
                    >
                      {formData.showPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {errors.password && (
                    <span style={errorStyle}>{errors.password}</span>
                  )}
                </div>
                <div style={inputRowStyleChild}>
                  <label style={labelStyle}>Confirm Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={formData.showConfirmPassword ? "text" : "password"}
                      name="password_confirmation"
                      placeholder="Re-enter your password"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      style={
                        errors.password_confirmation
                          ? errorInputStyle
                          : inputStyle
                      }
                      disabled={isLoading}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        padding: "10px", // Increased touch target for iOS
                      }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          showConfirmPassword: !prev.showConfirmPassword,
                        }))
                      }
                    >
                      {formData.showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {errors.password_confirmation && (
                    <span style={errorStyle}>
                      {errors.password_confirmation}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Checkbox */}
            <div
              style={checkboxContainerStyle}
              className="justify-content-center"
            >
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={isLoading}
                style={{ width: '20px', height: '20px' }} // Larger touch target
              />
              <span style={{ color: "#000000" }}>
                By clicking continue you accept our{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  style={{
                    ...linkStyle,
                    color: "#052C65",
                    textDecoration: "none",
                    fontWeight: "bold",                    
                  }}
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="/terms-conditions"
                  target="_blank"
                  style={{
                    ...linkStyle,
                    color: "#052C65",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                >
                  Terms & Conditions
                </a>
              </span>
            </div>

            {/* reCAPTCHA */}
            <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", flexDirection: "column" }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={onCaptchaChange}
              />
            </div>

            <div className="d-flex justify-content-center">
              <button
                type="submit"
                style={{
                  ...buttonStyle,
                  backgroundColor: isButtonHovered ? "#1a1aa0" : "#000080",
                  opacity: isLoading ? 0.7 : 1,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
                onMouseEnter={() => !isLoading && setIsButtonHovered(true)}
                onMouseLeave={() => !isLoading && setIsButtonHovered(false)}
                onTouchStart={(e) => {
                  if (isIOS) {
                    e.preventDefault();
                  }
                  if (!isLoading) {
                    setIsButtonHovered(true);
                  }
                }}
                onTouchEnd={handleTouchEnd}
                disabled={isLoading}
              >
                {isLoading ? "Creating Account..." : "Create Account"}
              </button>
            </div>
          </form>

          <div style={loginLinkContainerStyle}>
            <span
              style={{
                color: "#444444",
                fontWeight: "semibold",
                fontSize: "16px",
              }}
            >
              Already have an account?{" "}
            </span>
            <a
              href="/sign_in"
              style={{ ...loginLinkStyle, textDecoration: "none" }}
            >
              Login here
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CustomerForm;